import React from 'react';
import { Link } from 'react-router-dom';
import { SEOHead } from '../components/SEOHead';

const LAST_UPDATED = '24 April 2026';

export const Terms = () => {
  return (
    <div className="min-h-screen bg-slate-50">
      <SEOHead
        title="Terms of Use | Web Speed Advisor"
        description="Terms of use for Web Speed Advisor, including permitted use, service limitations and acceptable use obligations."
        canonical="https://webspeedadvisor.com/terms"
        robots="noindex, nofollow, noarchive"
      />

      <div className="bg-slate-900 text-white py-12">
        <div className="max-w-4xl mx-auto px-4">
          <h1 className="text-4xl md:text-5xl font-black font-chivo">Terms of Use</h1>
          <p className="text-slate-300 mt-2">Last reviewed: {LAST_UPDATED}</p>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-12 prose prose-lg prose-slate">
        <p>
          These Terms of Use govern your access to and use of Web Speed Advisor. By using the service,
          you agree to these terms. If you do not agree, you should not use the service.
        </p>

        <h2>Service Scope</h2>
        <p>
          Web Speed Advisor provides website performance analysis, summary insights and related implementation guidance.
          The service may change from time to time without prior notice.
        </p>

        <h2>Permitted Use</h2>
        <p>You may use the service only for lawful purposes and only in relation to websites you own or are authorised to assess.</p>
        <ul>
          <li>internal diagnostics and optimisation work;</li>
          <li>client, agency or consultancy analysis where you have permission;</li>
          <li>research, benchmarking and educational use; and</li>
          <li>reasonable commercial use consistent with these terms.</li>
        </ul>

        <h2>Prohibited Conduct</h2>
        <p>You must not:</p>
        <ul>
          <li>submit websites where you do not have authority or a legitimate reason to assess them;</li>
          <li>abuse, overload, disrupt or attempt to degrade the service through excessive or malicious traffic;</li>
          <li>attempt to reverse engineer, interfere with or compromise the service, its infrastructure or related systems;</li>
          <li>use the service in breach of any applicable law, regulation or third-party rights; or</li>
          <li>use automated means to scrape, duplicate or republish the service itself without permission.</li>
        </ul>

        <h2>GitHub Integration</h2>
        <p>
          When you authorise GitHub access, you grant Web Speed Advisor permission to read repository file
          contents solely for the purpose of generating draft pull requests. The service will only modify
          files within the scope of the fix type you have selected. You can revoke access at any time via
          your GitHub account settings.
        </p>

        <h2>AI-Generated Output</h2>
        <p>
          Recommendations, code snippets, and draft pull requests are generated by AI and may contain errors
          or inaccuracies. You are responsible for reviewing all AI-generated content carefully before
          applying it to a production environment. Nothing in the service output constitutes professional
          or technical advice; all outputs should be treated as a starting point for your own review.
        </p>

        <h2>Beta Features</h2>
        <p>
          The draft pull request feature is currently in beta. Beta features may change, be withdrawn, or
          produce unexpected results without notice. They should not be relied upon in automated pipelines
          without human review. We make no guarantees regarding the stability, completeness, or correctness
          of beta functionality.
        </p>

        <h2>Third-Party Dependencies</h2>
        <p>
          The service relies on third-party providers, including performance analysis APIs, AI systems and analytics tools.
          We are not responsible for downtime, output changes or restrictions imposed by those providers.
        </p>

        <h2>Availability and Accuracy</h2>
        <p>
          We aim to provide a reliable and useful service, but we do not guarantee uninterrupted availability,
          complete accuracy or suitability for every use case. Performance metrics and recommendations are informative
          and may vary by environment, geography, device, cache state and third-party provider behaviour.
        </p>

        <h2>No Professional Advice</h2>
        <p>
          Outputs from the service, including AI-generated content, are provided for general informational purposes only.
          You remain responsible for reviewing, testing and validating any recommendation or code before production use.
        </p>

        <h2>Fair Use and Rate Limiting</h2>
        <p>
          We may apply rate limits, temporary restrictions or technical controls to protect service stability and fair access.
          We may suspend or restrict access where usage appears abusive, excessive or harmful.
        </p>

        <h2>Intellectual Property</h2>
        <p>
          The service, including its branding, interface, original content and underlying materials, remains our property
          or the property of our licensors. These terms do not grant you any ownership rights in the service.
        </p>

        <h2>Disclaimer of Warranties</h2>
        <p>
          The service is provided on an "as is" and "as available" basis. To the fullest extent permitted by law,
          we disclaim warranties of any kind, whether express, implied or statutory.
        </p>

        <h2>Limitation of Liability</h2>
        <p>
          To the fullest extent permitted by law, we will not be liable for any indirect, incidental, consequential,
          special or business losses arising from or connected with your use of the service. Your use of the service
          is at your own risk.
        </p>

        <h2>Changes To These Terms</h2>
        <p>
          We may update these Terms of Use from time to time. Continued use of the service after an updated version
          is published will constitute acceptance of the revised terms.
        </p>

        <h2>Contact</h2>
        <p>
          If you have questions about these Terms of Use, please contact us through the website contact route
          or the relevant support channel made available on the site.
        </p>

        <div className="mt-8">
          <Link to="/" className="text-primary hover:text-primary-hover font-semibold">
            ← Back to Home
          </Link>
        </div>
      </div>
    </div>
  );
};

export default Terms;
